import { KiteIcon } from '@kite/react-kite';
import fetch from 'node-fetch';
import { GatsbyImage, IGatsbyImageData } from 'gatsby-plugin-image';
import React, { useEffect, useState } from 'react';
import { ReachButton } from '../ReachButton/ReachButton';
import {
    VideoPlayerProps,
    VideoPlayerTitleProps,
} from '../VideoPlayer/VideoPlayer';
import { processLink } from '../../shared/processLink';
import { AccentedText } from '../AccentedText/AccentedText';
import { getVideoId } from '../../shared/getVideoId';
import { ParentNodeProvider } from '../../providers/ParentNodeProvider';
import { ThreeColumnHeading } from './ThreeColumnHeading';

export interface ThreeColumnWideColumnProps {
    image?: IGatsbyImageData;
    imageAlt?: string;
    link?: {
        uri: string;
        title: string;
    };
    imageTitle?: string;
    text?: string;
    heading?: string;
    variant?: `featured-award` | undefined;
    video?: string;
    thumbnail?: IGatsbyImageData;
    thumbnailAlt?: string;
    title?: string;
    headingSize?: `small` | `medium` | `large`;
    setVideoOpen?: React.Dispatch<React.SetStateAction<VideoPlayerProps>>;
    setVideoTitle?: React.Dispatch<React.SetStateAction<VideoPlayerTitleProps>>;
}

export const ThreeColumnWideColumn = (props: ThreeColumnWideColumnProps) => {
    const [videoData, setVideoData] = useState(null);
    const { heading, headingSize } = props;
    const onVideoClick = (evt) => {
        props.setVideoOpen({
            videoUrl: props.video,
            setVideoOpen: props.setVideoOpen,
        });
        props.setVideoTitle({
            videoTitle: props.title,
            setVideoTitle: props.setVideoTitle,
        });
    };
    // Use Vimeo API to retrieve video thumbs and descriptions for videos WITHOUT embedded thumbnails
    useEffect(() => {
        const url = `https://vimeo.com/api/v2/video/${getVideoId(
            props.video
        )}.json`;
        const fetchVimeoData = async () => {
            try {
                const resp = await fetch(url);
                const data = await resp.json();
                data.map((d) => {
                    setVideoData(d);
                });
            } catch (error) {
                console.error(`Fetch error: `, error);
            }
        };
        if (!props.thumbnail && props.video) {
            fetchVimeoData();
        }
    }, []);

    return (
        <ParentNodeProvider nodeName={props.heading} nodeType="Three_Column">
            <div className={`column ${props.variant ?? ``}`}>
                {heading && (
                    <ThreeColumnHeading headingSize={headingSize}>
                        <AccentedText>{heading}</AccentedText>
                    </ThreeColumnHeading>
                )}

                {props.text && (
                    <div
                        className="text"
                        dangerouslySetInnerHTML={{
                            __html: props.text,
                        }}
                    ></div>
                )}
                {props.link && (
                    <div className="buttons">
                        <ReachButton
                            href={processLink(props.link.uri)}
                            buttonStyle="borderless"
                            buttonIcon={{
                                icon: `ki-arrow-right`,
                                position: `right`,
                            }}
                        >
                            {props.link.title}
                        </ReachButton>
                    </div>
                )}
                {props.image && (
                    <div className="image-wrapper">
                        <GatsbyImage
                            image={props.image}
                            alt={props.imageAlt ?? ``}
                        />
                        {props.imageTitle && (
                            <span className="image-title">
                                {props.imageTitle}
                            </span>
                        )}
                    </div>
                )}

                {props.video && (
                    <div className="video-thumbnail" onClick={onVideoClick}>
                        {props.thumbnail ? (
                            <GatsbyImage
                                objectFit="cover"
                                alt={props.thumbnailAlt ?? ``}
                                image={props.thumbnail}
                                className="thumbnail"
                            />
                        ) : (
                            <img
                                src={videoData?.thumbnail_large}
                                alt={videoData?.description}
                                loading="lazy"
                            />
                        )}
                        <button
                            className="play-icon"
                            type="button"
                            aria-label={`Play${
                                props.heading ? ` ${props.heading} ` : ` `
                            }Video`}
                        >
                            <KiteIcon icon="ki-play-f" size="2.375rem" />
                        </button>
                    </div>
                )}
            </div>
        </ParentNodeProvider>
    );
};
